import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, firstValueFrom } from 'rxjs';
import { LogService } from './log.service';
import { Logger } from '@unleash-tech/js-logger';

export type ErrorType = 'loadingError' | 'unsupportedFileType' | 'PasswordException' | 'fileTooBig' | 'unEnablePreview';

@Injectable({
  providedIn: 'root',
})
export class StreamContentService {
  private logger: Logger;

  constructor(log: LogService, private http: HttpClient) {
    this.logger = log.scope('StreamContentService');
  }

  async streamBlobSrc(url: string, streamLimitInMB?: number): Promise<string> {
    let stream;
    try {
      stream = await this.streamSrc(url, 'blob', streamLimitInMB);
    } catch (err) {
      return Promise.reject(err);
    }
    let blob = stream as Blob;
    if (!(stream instanceof Blob)) {
      const response = new Response(stream);
      blob = await response.blob();
    }
    return URL.createObjectURL(blob);
  }

  async streamTextSrc(url: string, streamLimitInMB?: number) {
    let stream;
    try {
      stream = (await this.streamSrc(url, 'text', streamLimitInMB)) as ReadableStream;
    } catch (err) {
      return Promise.reject(err);
    }
    const response = new Response(stream);
    return response.text();
  }

  streamSrc(url: string, type: 'text' | 'blob' = 'blob', streamLimitInMB?: number): Promise<ReadableStream | Blob> {
    if (!url.startsWith('https')) {
      return this.loadLocalPreview(url, type);
    }
    return fetch(url)
      .then((response: Response) => {
        const contentLength = +response.headers.get('content-length');
        const body: ReadableStream<Uint8Array> = response?.body;

        if (!body || response.status !== 200) {
          throw 'loadingError' as ErrorType;
        } else {
          const reader = body.getReader();
          if (streamLimitInMB && this.contentLengthToMB(contentLength) > streamLimitInMB) {
            reader.cancel('fileTooBig');
            throw 'fileTooBig' as ErrorType;
          }
          return new ReadableStream({
            start(controller) {
              return pump();

              function pump() {
                return reader.read().then(({ done, value }) => {
                  if (done) {
                    controller.close();
                    return;
                  }
                  controller.enqueue(value);
                  return pump();
                });
              }
            },
          });
        }
      })
      .catch((err) => {
        throw err;
      });
  }

  private async loadLocalPreview(url: string, type: 'text' | 'blob') {
    if (type === 'text') {
      let res: Observable<string>;
      try {
        res = this.http.get(url, { responseType: 'text' });
      } catch (error) {
        this.logger.error('got error trying to load local text file', { url, error });
        throw error;
      }
      return new ReadableStream({
        start(controller) {
          function push() {
            if (!res) {
              this.logger.error('loadLocalPreview - res is undefined');
              controller.close();
              return;
            }
            res.subscribe({
              next: (data) => {
                const encoded = new TextEncoder().encode(data);
                controller.enqueue(encoded);
              },
              complete: () => {
                controller.close();
              },
            });
          }
          push();
        },
      });
    } else {
      try {
        const res = this.http.get(url, { responseType: 'blob' });
        return firstValueFrom(res).then((r) => r as Blob);
      } catch (error) {
        this.logger.error('got error trying to load local blob file', { url, error });
        throw error;
      }
    }
  }

  private contentLengthToMB(contentLength: number): number {
    return contentLength / 1024 / 1024;
  }
}
